/**
 * The TRACE clock — NULLRUN's signature mechanic.
 *
 * Trace rises a baseline each turn and more on risky/aggressive actions. As it
 * climbs it escalates the threat at fixed tiers, and once it reaches max a hard
 * countdown begins; when the countdown expires the run takes lethal-class
 * pressure (handled in combat.ts as a big unblockable hit + buffed ICE).
 *
 * Pure module: takes a TraceState, returns a new one + structured events. No
 * RNG, no DOM. Tunable constants centralized here.
 */

import type { CombatEvent, TraceState } from "./types";

export const TRACE_TUNING = {
  defaultMax: 16,
  defaultPerTurn: 1,
  /**
   * Escalation tiers: when trace crosses one of these thresholds (first time),
   * a `traceEscalate` event fires with the tier index. combat.ts reads the tier
   * to apply effects (ICE gains strength / a reinforcement / harder intents).
   */
  tiers: [6, 11, 16] as const,
  /** Turns of hard countdown once trace reaches max. */
  hardCountdownTurns: 3,
  /** Unblockable damage dealt to the runner when the hard countdown expires. */
  countdownLethalDamage: 999,
} as const;

export function newTraceState(
  max: number = TRACE_TUNING.defaultMax,
  perTurn: number = TRACE_TUNING.defaultPerTurn,
): TraceState {
  return {
    value: 0,
    max,
    perTurn,
    triggeredTiers: [],
    hardCountdown: -1,
  };
}

function cloneTrace(t: TraceState): TraceState {
  return {
    value: t.value,
    max: t.max,
    perTurn: t.perTurn,
    triggeredTiers: t.triggeredTiers.slice(),
    hardCountdown: t.hardCountdown,
  };
}

/**
 * Add (or subtract, if negative) trace. Clamps to [0, max]. Detects newly
 * crossed escalation tiers and whether the hard countdown should start.
 * Returns the new state and the events generated (trace + traceEscalate).
 */
export function addTrace(
  t: TraceState,
  delta: number,
  reason: string,
): { trace: TraceState; events: CombatEvent[] } {
  const next = cloneTrace(t);
  const events: CombatEvent[] = [];
  const before = next.value;
  next.value = Math.max(0, Math.min(next.max, next.value + delta));
  if (next.value !== before) {
    events.push({
      t: "trace",
      value: next.value,
      delta: next.value - before,
      reason,
    });
  }

  // Tier escalations (only when increasing, only first time per tier).
  if (delta > 0) {
    for (let i = 0; i < TRACE_TUNING.tiers.length; i++) {
      const threshold = TRACE_TUNING.tiers[i]!;
      if (next.value >= threshold && !next.triggeredTiers.includes(i)) {
        next.triggeredTiers.push(i);
        events.push({ t: "traceEscalate", tier: i });
      }
    }
  }

  // Start the hard countdown the first time we hit max.
  if (next.value >= next.max && next.hardCountdown < 0) {
    next.hardCountdown = TRACE_TUNING.hardCountdownTurns;
  }

  return { trace: next, events };
}

/**
 * Tick the hard countdown by one (called once per enemy turn). Returns the new
 * state and whether the countdown JUST expired this tick (caller applies the
 * lethal consequence). Does nothing if the countdown hasn't started.
 */
export function tickHardCountdown(t: TraceState): {
  trace: TraceState;
  expired: boolean;
} {
  if (t.hardCountdown < 0) return { trace: t, expired: false };
  const next = cloneTrace(t);
  next.hardCountdown -= 1;
  if (next.hardCountdown <= 0) {
    next.hardCountdown = 0;
    return { trace: next, expired: true };
  }
  return { trace: next, expired: false };
}

/** Convenience: is the hard countdown active (started, not yet expired)? */
export function isCountdownActive(t: TraceState): boolean {
  return t.hardCountdown > 0;
}
